import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {DisplayType, Placement, TooltipArrowStyleOverride, TooltipState, TooltipWrapperStyle} from './types';
import {template} from './template';
import css from './index.styl?inline';

export interface TooltipJsxProps {
  attrs?: {
    'always-show'?: boolean;
    delay?: number;
    'max-width'?: string;
    offset?: number;
    placement?: Placement;
    type?: DisplayType;
  };
}

const VISIBILITY_HIDDEN = `hidden`;
const VISIBILITY_TRANSITIONING = `transitioning`;
const VISIBILITY_VISIBLE = `visible`;

const MIN_BUFFER_FROM_EDGE = 4; // px;

/**
 * When a delay is provided, after a tooltip is shown with initial delay, hovering over neighbor
 * elements with tooltips will not have a delay effect. The behavior is maintained with this global flag.
 */
let shouldDelayTooltip = true;

export class Tooltip extends Component<TooltipState, TooltipJsxProps['attrs']> {
  public _listeners;
  public _parentEl;
  public _pendingPositionTooltipRaf;
  public _pendingPositionTooltipTimeout;

  static get attrsSchema(): AttrsSchema<TooltipJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'always-show': `boolean`,
      offset: {type: `number`, default: 8},
      'max-width': `string`,
      placement: {type: `string`, default: Placement.Top, enum: Object.values(Placement)},
      type: {type: `string`, default: DisplayType.Default, enum: Object.values(DisplayType)},
      delay: {type: `number`, description: `value in ms to delay showing tooltip`},
    };
  }

  constructor() {
    super();
    // track added listeners so we can remove them in disconnectedCallback and be GC friendly
    this._listeners = new Map();
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  static get PLACEMENTS() {
    return Object.values(Placement);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        visibility: VISIBILITY_HIDDEN,
        placement: ``,
        wrapperStyles: {
          left: `0px`,
          top: `0px`,
        },
        arrowStyleOverrides: {},
        isFirstActiveTooltip: true,
      },
      helpers: {
        VISIBILITY_HIDDEN,
        VISIBILITY_TRANSITIONING,
        VISIBILITY_VISIBLE,
        stopPropagation: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        },
        getTooltipEl: () => this.el.querySelector(`.mp-tooltip-main`),
        shouldAnimateTooltip: () => !!this.attr(`delay`) && this.state.isFirstActiveTooltip,
      },
    };
  }

  show() {
    this.update({
      visibility: VISIBILITY_TRANSITIONING,
    });

    this.cancelPendingPositionTooltipIfNeeded();

    const delay = this.attr(`delay`);
    const makeTooltipVisible = () => {
      const tooltipEl = this.helpers.getTooltipEl();
      this._positionTooltip(tooltipEl);
      if (delay && !this.attr(`always-show`)) {
        this.update({isFirstActiveTooltip: shouldDelayTooltip});
        shouldDelayTooltip = false;
      }

      this.update({
        visibility: VISIBILITY_VISIBLE,
      });
    };

    if (delay && shouldDelayTooltip) {
      this._pendingPositionTooltipTimeout = window.setTimeout(() => {
        makeTooltipVisible();
      }, delay);
    } else {
      this._pendingPositionTooltipRaf = window.requestAnimationFrame(() => {
        makeTooltipVisible();
      });
    }
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  _positionTooltip(tooltipEl) {
    if (tooltipEl) {
      // Since computation of top/left offsets depend on tooltip's height/width, these offsets must be computed
      // after tooltip is visible, hence in requestAnimationFrame.

      let parentOffset;
      const parentPosition = window.getComputedStyle(this.parentEl).getPropertyValue(`position`);
      // The tooltip has position absolute, which means it will be positioned relative to the nearest ancestor with
      // non-static position. Different handling is needed depending on whether the direct parent of the tooltip
      // has non-static positioning.
      if (parentPosition === `static`) {
        const offsetBoundingRect = this.parentEl.offsetParent.getBoundingClientRect();
        const parentBoundingRect = this.parentEl.getBoundingClientRect();
        parentOffset = {
          left: parentBoundingRect.left - offsetBoundingRect.left,
          top: parentBoundingRect.top - offsetBoundingRect.top,
        };
      } else {
        parentOffset = {
          left: 0,
          top: 0,
        };
      }

      const wrapperStyles: TooltipWrapperStyle = {};
      const arrowStyleOverrides: TooltipArrowStyleOverride = {};

      const offset = this.attr(`offset`);
      const placement = this.attr(`placement`);

      if (placement === Placement.Top || placement === Placement.Bottom) {
        /**                 viewport
         *  --------------------------------------------------
         *  |                                                |
         *  |       < t o o l t i p    c o n t e n t >       |
         *  |                       V                        |
         *  |                   < parent >                   |
         *  |       { overhang }          { overhang }       |
         *  |{ available space }          { available space }|
         *  |                                                |
         *  --------------------------------------------------
         */
        const parentBounds = this.parentEl.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth;
        const overhang = (tooltipEl.offsetWidth - this.parentEl.offsetWidth) / 2;
        const availableSpaceToTheLeft = parentBounds.x - MIN_BUFFER_FROM_EDGE;
        const availableSpaceToTheRight =
          window.scrollX + viewportWidth - (parentBounds.x + parentBounds.width) - MIN_BUFFER_FROM_EDGE;
        let xAxisAdjustment = 0;
        let overhangLeft = overhang;

        // If the tooltip will go outside of the right bound of the viewport, adjust it left so it won't
        if (overhang > availableSpaceToTheRight) {
          xAxisAdjustment = availableSpaceToTheRight - overhang;
          overhangLeft = overhang - xAxisAdjustment;
        }

        // Now, if the tooltip will go outside of the left bound of the viewport, adjust it right so it won't
        if (overhangLeft > availableSpaceToTheLeft) {
          xAxisAdjustment = overhang - availableSpaceToTheLeft;
        }

        wrapperStyles.left = `${parentOffset.left - overhang + xAxisAdjustment}px`;
        arrowStyleOverrides.left = `calc(50% - ${xAxisAdjustment}px)`;

        if (placement === Placement.Top) {
          wrapperStyles.top = `${parentOffset.top - tooltipEl.offsetHeight - offset}px`;
        } else {
          wrapperStyles.top = `${parentOffset.top + this.parentEl.offsetHeight + offset}px`;
        }
      } else if (placement === Placement.Left || placement === Placement.Right) {
        if (tooltipEl.offsetHeight > this.parentEl.offsetHeight) {
          wrapperStyles.top = `${parentOffset.top}px`;
        } else {
          wrapperStyles.top = `${parentOffset.top + (this.parentEl.offsetHeight - tooltipEl.offsetHeight) / 2}px`;
        }

        if (placement === Placement.Left) {
          wrapperStyles.left = `${parentOffset.left - tooltipEl.offsetWidth - offset}px`;
        } else {
          wrapperStyles.left = `${parentOffset.left + this.parentEl.offsetWidth + offset}px`;
        }
      }

      this.update({
        wrapperStyles,
        arrowStyleOverrides,
      });
    }
  }

  hide(ev: MouseEvent) {
    this.cancelPendingPositionTooltipIfNeeded();

    /**
     * relatedTarget will be the element that the mouse has hovered on to after parentElement.
     * If that element does not have a tooltip child, we reset the flag so that the next tooltip can be delayed.
     * */
    if (!ev.relatedTarget || (ev.relatedTarget && !hasTooltipChildOrSibling(ev.relatedTarget as HTMLElement))) {
      shouldDelayTooltip = true;
    }

    this.update({visibility: VISIBILITY_HIDDEN});
  }

  /**
   * Used for adding/removing event listeners
   */
  get parentEl() {
    return this._parentEl || this.parentNode;
  }

  set parentEl(parentEl) {
    this._parentEl = parentEl;
  }

  connectedCallback() {
    super.connectedCallback();

    // From MDN doc https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements
    // "connectedCallback may be called once your element is no longer connected, use
    // Node.isConnected to make sure."
    // This is happening when dragging query entry rows in the query builder. As a result,
    // parentNode is unexpectedly null, leading to a JS exception.
    if (!this.isConnected) {
      return;
    }

    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (this.initialized) {
      this.render();
    }
  }

  render() {
    this.removeListenersIfNeeded();
    if (this.attr(`always-show`)) {
      this.show();
    } else {
      if (this.state.visibility === VISIBILITY_VISIBLE) {
        this.show();
      }
      this._addEventListener(this.parentEl, `mouseenter`, this.show);
      this._addEventListener(this.parentEl, `mouseleave`, this.hide);
    }
  }

  _addEventListener(el, event, listener) {
    el.addEventListener(event, listener);
    if (!this._listeners.has(el)) {
      this._listeners.set(el, {[event]: listener});
    } else {
      this._listeners.get(el)[event] = listener;
    }
  }

  removeListenersIfNeeded() {
    for (const [el, listeners] of this._listeners.entries()) {
      for (const [event, listener] of Object.entries(listeners)) {
        el.removeEventListener(event, listener);
      }
    }
  }

  cancelPendingPositionTooltipIfNeeded() {
    if (this._pendingPositionTooltipRaf) {
      window.cancelAnimationFrame(this._pendingPositionTooltipRaf);
      this._pendingPositionTooltipRaf = null;
    }

    if (this._pendingPositionTooltipTimeout) {
      window.clearTimeout(this._pendingPositionTooltipTimeout);
      this._pendingPositionTooltipTimeout = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeListenersIfNeeded();
    this.cancelPendingPositionTooltipIfNeeded();
  }
}

/**
 * Check if element has a tooltip child or sibling. Uses the :scope selector which references the element itself in order
 * to find direct children. https://developer.mozilla.org/en-US/docs/Web/CSS/:scope
 * We check for siblings because there are certain cases where if you rapidly move the mouse the related target can end up
 * being a sibling of the tooltip, i.e. the label of a button.
 */
function hasTooltipChildOrSibling(el: HTMLElement): boolean {
  return !!(el.querySelector(`:scope > mp-tooltip`) || el.parentElement?.querySelector(`:scope > mp-tooltip`));
}

export const TooltipEl = defineAndMakeJsxFactory<TooltipJsxProps>(`mp-tooltip`, Tooltip);
